import bwipjs from 'bwip-js/browser';
import type { RenderOptions } from 'bwip-js/browser';
import drawsvg from './svg';

const MULTI_BARCODE_GAP = 20;
const MULTI_BARCODE_TEXT_GAP = 36;
const MULTI_BARCODE_TEXT_SIZE = 18;

/** Aspect ratio (width / height) for individual barcode in multi-barcode layouts */
const MULTI_BARCODE_ASPECT_RATIO = 3;

function renderBarcodeCanvas(
  options: RenderOptions,
  multiBarcode: boolean,
  areaAspectRatio = 0,
  showText = false
): HTMLCanvasElement {
  if (!multiBarcode) {
    const canvas = document.createElement('canvas');
    return bwipjs.toCanvas(canvas, options) as HTMLCanvasElement;
  }

  const texts = options.text.split(/\r\n|\r|\n/).filter(Boolean);
  const canvases = texts.map((text) => {
    const canvas = document.createElement('canvas');
    return bwipjs.toCanvas(canvas, { ...options, text });
  });

  const canvas = document.createElement('canvas');
  const naturalWidth = Math.max(...canvases.map((barcode) => barcode.width));
  const gap = showText ? MULTI_BARCODE_TEXT_GAP : MULTI_BARCODE_GAP;
  const contentHeight =
    canvases.reduce(
      (height, barcode) => height + barcode.width / MULTI_BARCODE_ASPECT_RATIO,
      gap * (canvases.length - 1)
    ) + (showText ? gap : 0);
  canvas.width = Math.ceil(
    showText && areaAspectRatio > 0
      ? Math.max(naturalWidth, contentHeight / areaAspectRatio)
      : naturalWidth
  );
  canvas.height = Math.ceil(
    Math.max(contentHeight, canvas.width * areaAspectRatio)
  );
  const context = canvas.getContext('2d')!;
  if (showText) {
    context.font = `${MULTI_BARCODE_TEXT_SIZE}px sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'alphabetic';
  }
  let y = 0;
  for (const [index, barcode] of canvases.entries()) {
    const barcodeHeight = barcode.width / MULTI_BARCODE_ASPECT_RATIO;
    context.drawImage(
      barcode,
      (canvas.width - barcode.width) / 2,
      y,
      barcode.width,
      barcodeHeight
    );
    if (showText) {
      context.fillText(
        texts[index],
        canvas.width / 2,
        y + barcodeHeight + MULTI_BARCODE_TEXT_SIZE + 4
      );
    }
    y += barcodeHeight + gap;
  }
  return canvas;
}

function renderBarcodeSvg(type: string, text: string): string {
  const opts: RenderOptions & { dontlint: boolean } = {
    bcid: type, // Barcode type
    text, // Text to encode
    scale: 3, // 3x scaling factor
    height: 20, // Bar height, in millimeters
    includetext: false, // Show human-readable text
    textxalign: 'center', // Always good to set this
    dontlint: true,
  };
  return bwipjs.render(opts, drawsvg(opts, bwipjs.FontLib));
}

function getSvgDimensions(svg: string): {
  width: number;
  height: number;
  content: string;
} {
  const root = svg.match(/^(<svg\b[^>]*>)([\s\S]*)<\/svg>\s*$/);
  const openingTag = root?.[1];
  const width = Number(openingTag?.match(/\bwidth="([\d.]+)"/)?.[1]);
  const height = Number(openingTag?.match(/\bheight="([\d.]+)"/)?.[1]);
  if (!root || !width || !height) {
    throw new Error('Could not determine barcode SVG dimensions.');
  }
  return { width, height, content: root[2] };
}

function escapeSvgText(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&apos;',
    };
    return entities[character];
  });
}

function stackBarcodeSvgs(
  svgs: string[],
  areaAspectRatio = 0,
  showText = false,
  texts: string[] = []
): string {
  const dimensions = svgs.map(getSvgDimensions);
  const naturalWidth = Math.max(...dimensions.map((size) => size.width));
  const gap = showText ? MULTI_BARCODE_TEXT_GAP : MULTI_BARCODE_GAP;
  const contentHeight =
    dimensions.reduce(
      (sum, size) => sum + size.width / MULTI_BARCODE_ASPECT_RATIO,
      gap * (svgs.length - 1)
    ) + (showText ? gap : 0);
  // Match the item ratio with whitespace when the captions need more height.
  const width =
    showText && areaAspectRatio > 0
      ? Math.max(naturalWidth, contentHeight / areaAspectRatio)
      : naturalWidth;
  const height = Math.max(contentHeight, width * areaAspectRatio);
  let y = 0;
  const children = dimensions.map((size, index) => {
    const x = (width - size.width) / 2;
    const barcodeHeight = size.width / MULTI_BARCODE_ASPECT_RATIO;
    const positionedSvg = `<g transform="translate(${x} ${y}) scale(1 ${barcodeHeight / size.height})">${size.content}</g>`;
    const caption = showText
      ? `<text x="${width / 2}" y="${y + barcodeHeight + MULTI_BARCODE_TEXT_SIZE + 4}" text-anchor="middle" font-family="sans-serif" font-size="${MULTI_BARCODE_TEXT_SIZE}">${escapeSvgText(texts[index] ?? '')}</text>`
      : '';
    y += barcodeHeight + gap;
    return positionedSvg + caption;
  });

  return `<svg version="1.1" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
${children.join('')}</svg>\n`;
}

export { renderBarcodeCanvas, renderBarcodeSvg, stackBarcodeSvgs };
